import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Modal,
  Text,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconEye, IconX } from "@tabler/icons";
import { MantineReactTable } from "mantine-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "utilities/Toast";

import AppBreadcrumb from "components/AppBreadcrumb";
import moment from "moment";
import { httpClient } from "lib/axios";

export default function BatchMergeApprovals() {
  let isMobileScreen = useMediaQuery("(max-width: 768px)");
  const queryClient = useQueryClient();

  const [viewModal, setViewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isActioning, setIsActioning] = useState(false);

  // ── Column filters & pagination ─────────────────────────────────────────
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Reset to first page whenever filters change
  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [columnFilters]);

  const { data, isLoading, isRefetching } = useQuery({
    queryKey: ["batch-merge-requests", pagination.pageIndex, pagination.pageSize, columnFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page_number", pagination.pageIndex + 1);
      params.set("page_size", pagination.pageSize);
      // Use LOCAL date parts to avoid UTC timezone shift (IST offset issue)
      const toYMD = (v) => {
        if (!v) return "";
        const d = v instanceof Date ? v : new Date(v);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };
      columnFilters.forEach(({ id, value }) => {
        if (value === null || value === undefined || value === "") return;
        if (id === "created_at") {
          const [from, to] = Array.isArray(value) ? value : [value, null];
          const fromStr = from ? toYMD(from) : "";
          const toStr   = to   ? toYMD(to)   : "";
          if (fromStr) params.set("date_from", fromStr);
          // If only one date entered → filter that single day
          if (toStr)   params.set("date_to",   toStr);
          else if (fromStr) params.set("date_to", fromStr);
        } else if (id === "counter_name")    params.set("counter",      value);
        else if (id === "product_title")     params.set("product",      value);
        else if (id === "target_batch_no")   params.set("target_batch", value);
        else if (id === "status")            params.set("status",       value);
      });
      const res = await httpClient.get(`batch_merge/?${params.toString()}`);
      return res.data;
    },
    keepPreviousData: true,
  });

  const handleAction = async (requestId, action) => {
    setIsActioning(true);
    try {
      const res = await httpClient.post(`batch_merge_accept/`, {
        request_id: requestId,
        action: action,
      });
      if (res.data.success) {
        showSuccessToast({ title: "Success", message: res.data.message });
        queryClient.invalidateQueries("batch-merge-requests");
        setViewModal(false);
      } else {
        showErrorToast({ title: "Error", message: res.data.message });
      }
    } catch (e) {
      showErrorToast({ title: "Error", message: "Failed to process request." });
    } finally {
      setIsActioning(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Date",
        accessorKey: "created_at",
        size: "auto",
        filterVariant: "date-range",
        Cell: ({ cell }) => moment(cell.getValue()).format("DD-MM-YYYY hh:mm A"),
      },
      {
        header: "Counter",
        accessorKey: "counter_name",
        size: "auto",
      },
      {
        header: "Product",
        accessorKey: "product_title",
        size: "auto",
      },
      {
        header: "Target Batch",
        accessorKey: "target_batch_no",
        size: "auto",
        Cell: ({ cell }) => (
          <Badge size="sm" color="blue" variant="light" fw={600}>
            {cell.getValue()}
          </Badge>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        size: "auto",
        Cell: ({ cell }) => (
          <Badge
            color={
              cell.getValue() === "pending"
                ? "yellow"
                : cell.getValue() === "accepted"
                  ? "green"
                  : "red"
            }
            variant="light"
            fw={600}
          >
            {cell.getValue()?.toUpperCase()}
          </Badge>
        ),
      },
      {
        header: "Actions",
        size: "auto",
        Cell: ({ row }) => (
          <Flex gap={8}>
            <Tooltip label="View Details">
              <ActionIcon
                color="blue"
                variant="light"
                onClick={() => {
                  setSelectedRequest(row.original);
                  setViewModal(true);
                }}
              >
                <IconEye size={18} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        ),
      },
    ],
    []
  );

  const isPending = selectedRequest?.status === "pending";
  const isAccepted = selectedRequest?.status === "accepted";

  // Compute before/after quantities correctly based on status
  const totalMergeQty =
    selectedRequest?.items?.reduce((sum, it) => sum + (it.merged_qty || 0), 0) || 0;
  const currentStock = selectedRequest?.target_batch_current_stock ?? 0;

  // For PENDING:  currentStock = before,  afterStock = current + totalMerge
  // For ACCEPTED: currentStock = after,   beforeStock = current - totalMerge (merge already applied)
  // For REJECTED: stock unchanged, no arrow needed
  const beforeStock = isAccepted ? currentStock - totalMergeQty : currentStock;
  const afterStock = isAccepted ? currentStock : currentStock + totalMergeQty;

  return (
    <>
      <Container fluid>
        <Box p={isMobileScreen ? 5 : 25}>
          <Flex justify={"space-between"} align="end">
            <Box>
              <Text size={"lg"} fw={600} mb={10}>
                Batch Merge Approvals
              </Text>
              <AppBreadcrumb
                crumbs={[
                  { text: "Inventory" },
                  { text: "Batch Merge Approvals" },
                ]}
              />
            </Box>
          </Flex>
        </Box>
      </Container>

      <Container fluid mt={10} mb={30}>
        <Box px={isMobileScreen ? 5 : 25} sx={{ borderRadius: 2 }}>
          <Box className="datatable-wrapper">
            <MantineReactTable
              mantineTableProps={{ className: "datatable", striped: true }}
              initialState={{ density: "xs", showColumnFilters: false }}
              columns={columns}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnActions={false}
              enableGlobalFilter={true}
              data={data?.data || []}
              // ── server-side filtering ──
              manualFiltering
              onColumnFiltersChange={setColumnFilters}
              // ── server-side pagination ──
              manualPagination
              rowCount={data?.count || 0}
              onPaginationChange={(updater) => {
                setPagination((prev) => {
                  const next = typeof updater === "function" ? updater(prev) : updater;
                  return { ...prev, ...next };
                });
              }}
              state={{
                columnFilters,
                pagination,
                isLoading: isLoading || isRefetching,
              }}
            />
          </Box>
        </Box>
      </Container>

      {/* ─── Detail / Approval Modal ─── */}
      <Modal
        opened={viewModal}
        onClose={() => setViewModal(false)}
        title={
          <Box px={10}>
            <Flex align="center" gap={10}>
              <Text fw={700} size="md">
                {selectedRequest?.product_title}
              </Text>
              <Badge
                color={isPending ? "yellow" : isAccepted ? "green" : "red"}
                variant="light"
                fw={600}
              >
                {selectedRequest?.status?.toUpperCase()}
              </Badge>
            </Flex>
            <Text size="xs" color="dimmed" mt={2}>
              {selectedRequest?.counter_name} &nbsp;·&nbsp;{" "}
              {moment(selectedRequest?.created_at).format("DD-MM-YYYY hh:mm A")}
            </Text>
          </Box>
        }
        size={"720px"}
        centered
      >
        <Box px={10} pb={20}>
          <Divider mb={16} />

          {/* Legend */}
          <Flex gap={20} mb={16} px={2}>
            <Flex align="center" gap={6}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: "#e8f5e9",
                  border: "1.5px solid #43a047",
                }}
              />
              <Text size="xs" color="dimmed">
                Merge From (source)
              </Text>
            </Flex>
            <Flex align="center" gap={6}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: "#e3f2fd",
                  border: "1.5px solid #1e88e5",
                }}
              />
              <Text size="xs" color="dimmed">
                Merge To (target)
              </Text>
            </Flex>
          </Flex>

          {/* ── Source Batch Cards ── */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
            {selectedRequest?.items?.map((item) => (
              <Box
                key={item.id}
                sx={{
                  background: "#e8f5e9",
                  border: "1.5px solid #43a047",
                  borderRadius: 10,
                  padding: "12px 16px",
                }}
              >
                <Flex align="center" justify="space-between" wrap="wrap" gap={8}>
                  <Flex direction="column" gap={2}>
                    <Text size="xs" color="#2e7d32" fw={600} tt="uppercase">
                      Merge From (Source Batch)
                    </Text>
                    <Text fw={700} size="sm" color="#2e7d32">
                      {item.source_batch_no}
                    </Text>
                  </Flex>
                  <Flex align="center" gap={10}>
                    <Box sx={{ textAlign: "center" }}>
                      <Text size="xs" color="dimmed" mb={2}>
                        Qty to Transfer
                      </Text>
                      <Badge color="orange" variant="filled" fw={700} size="sm">
                        -{item.merged_qty}
                      </Badge>
                    </Box>
                    <Badge color="green" variant="light" size="xs">
                      Source
                    </Badge>
                  </Flex>
                </Flex>
              </Box>
            ))}
          </Box>

          {/* ── Merge Arrow ── */}
          <Flex justify="center" align="center" gap={6} mb={10}>
            <Box sx={{ flex: 1, height: 1, background: "#dee2e6" }} />
            <Text size="xs" color="dimmed" px={8} sx={{ whiteSpace: "nowrap" }}>
              ↓ merged into target ↓
            </Text>
            <Box sx={{ flex: 1, height: 1, background: "#dee2e6" }} />
          </Flex>

          {/* ── Target Batch Card with Before → After ── */}
          <Box
            sx={{
              background: "#e3f2fd",
              border: "1.5px solid #1e88e5",
              borderRadius: 10,
              padding: "14px 16px",
              marginBottom: 16,
            }}
          >
            <Flex align="center" justify="space-between" wrap="wrap" gap={10}>
              {/* Batch label */}
              <Flex direction="column" gap={3}>
                <Text size="xs" color="#1565c0" fw={600} tt="uppercase">
                  Merge To (Target Batch)
                </Text>
                <Text fw={700} size="sm" color="#1565c0">
                  {selectedRequest?.target_batch_no}
                </Text>
              </Flex>

              {/* Qty flow: Before → After */}
              <Flex align="center" gap={10}>
                {/* Before */}
                <Box
                  sx={{
                    textAlign: "center",
                    background: "#fff",
                    border: "1px solid #bbdefb",
                    borderRadius: 8,
                    padding: "4px 14px",
                    minWidth: 60,
                  }}
                >
                  <Text size="10px" color="dimmed" fw={600} tt="uppercase" mb={1}>
                    Before
                  </Text>
                  <Text fw={800} size="md" color="#1565c0">
                    {beforeStock}
                  </Text>
                </Box>

                {/* Plus indicator */}
                <Flex direction="column" align="center" gap={1}>
                  <Text size="xs" color="#43a047" fw={700}>
                    +{totalMergeQty}
                  </Text>
                  <Text size="lg" color="dimmed" lh={1}>
                    →
                  </Text>
                </Flex>

                {/* After */}
                <Box
                  sx={{
                    textAlign: "center",
                    background: isAccepted ? "#e8f5e9" : "#f1f8e9",
                    border: isAccepted ? "1.5px solid #43a047" : "1.5px dashed #81c784",
                    borderRadius: 8,
                    padding: "4px 14px",
                    minWidth: 60,
                  }}
                >
                  <Text
                    size="10px"
                    color={isAccepted ? "#2e7d32" : "dimmed"}
                    fw={600}
                    tt="uppercase"
                    mb={1}
                  >
                    {isAccepted ? "Final" : "Expected"}
                  </Text>
                  <Text fw={800} size="md" color={isAccepted ? "#2e7d32" : "#558b2f"}>
                    {afterStock}
                  </Text>
                </Box>

                <Badge color="blue" variant="light" size="xs">
                  Target
                </Badge>
              </Flex>
            </Flex>
          </Box>

          <Divider mb={14} />

          {/* ── Accept / Reject or Status Badge ── */}
          {isPending ? (
            <Flex justify="flex-end" gap={10}>
              <Button
                color="red"
                variant="light"
                leftIcon={<IconX size={16} />}
                loading={isActioning}
                onClick={() => handleAction(selectedRequest.id, "reject")}
              >
                Reject
              </Button>
              <Button
                color="green"
                leftIcon={<IconCheck size={16} />}
                loading={isActioning}
                onClick={() => handleAction(selectedRequest.id, "accept")}
              >
                Accept Merge
              </Button>
            </Flex>
          ) : (
            <Flex justify="flex-end">
              <Badge
                size="lg"
                color={isAccepted ? "green" : "red"}
                variant="light"
                fw={600}
                px={16}
                py={8}
              >
                {isAccepted ? "✓ Merge Accepted" : "✗ Merge Rejected"}
              </Badge>
            </Flex>
          )}
        </Box>
      </Modal>
    </>
  );
}
